<template>  
  <transition name="fade">
    <div
      v-if="!isResponseAreaVisible"
      class="text-center"
    >
      <button
        type="button"
        class="btn btn-lg btn-primary"
        @click="showResponseArea"
      >
        <span>{{ clientMessages.RespondToAppeal }}</span>
      </button>
    </div>
  </transition>
  <transition name="fade">
    <div
      v-if="isResponseAreaVisible"
      id="response-area"
    >
      <div
        v-if="!isResponseFormVisible && isCaptchaVisible"
        class="text-center"
      >
        <span
          class="sr-only"
          aria-live="polite"
        >Please complete the captcha to continue</span>
        <CaptchaWidget
          class="captcha-container"
          :public-key="captchaPublicKey"
          :language-code="languageCode"
          @captcha-verified="captchaCallback"
          @captcha-expired="captchaCallback"
        />
      </div>
      <ResponseForm
        v-if="isResponseFormVisible"
        :response-public-reference="responsePublicReference"
        :response-type="responseType"
        :client-messages="clientMessages"
      />
    </div>
  </transition>
  <transition name="fade">
    <h3
      v-if="!isResponseFormVisible && responseAreaLoading"
      class="loading-heading"
    >
      <i
        class="fa-solid fa-circle-notch fa-spin me-1"
        aria-hidden="true"
      />
      <strong><span>{{ clientMessages.Loading }}</span></strong>
    </h3>
  </transition>
  <ToastItems />
  <ModalContainer />
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import ContextState from '@/App/VueCore/stores/models/contextState';
import { useContextDataStore } from '@/App/VueCore/stores/contextDataStore';
import { PortalSettings } from '@/App/Types/interfaces/portalSettings';
import systemSettingsRepository from '@/App/Services/systemSettingsService';
import responseService from '@/App/Services/responseService';
import resourceDataService from '@/App/Services/resourceDataService';
import FlowWrapper from '@/App/Utils/flowWrapper';
import logger from '@/App/Utils/logger';
import constants from '@/App/constants';
import { ApplicationError } from '@/App/Services/clients/applicationError';

import CaptchaWidget from '@/App/VueComponents/captcha/CaptchaWidget.vue';
import ModalContainer from '@/App/VueComponents/modals/ModalContainer.vue';

import ResponseForm from '@/App/VueComponents/responseForm/ResponseForm.vue';
import ToastItems from '@/App/VueComponents/toast/ToastItems.vue';

const props = defineProps<{
  clientMessages: Record<string, string>,
  appealPublicReference: string,
}>();

const contextDataStore = useContextDataStore();

const isResponseAreaVisible = ref(false);
const isResponseFormVisible = ref(false);
const responseAreaLoading = computed(() => !isCaptchaVisible.value && isResponseFormVisible.value);
const languageCode = ref();
const isCaptchaEnabled = ref(false);
const captchaPublicKey = ref(null);
const isCaptchaVisible = ref(false);
const captchaValue = ref<string | null>(null);

const captchaCallback = (value: string | null) => {
  captchaValue.value = value;
  if (isCaptchaVisible.value && captchaValue.value !== null && captchaValue.value !== 'Captcha disabled') {
    isCaptchaVisible.value = false;
    showResponseForm();
  }
};

const responsePublicReference = ref(null);
const responseType = constants.responseParentType.appeal;

const showResponseArea = () => {
  isResponseAreaVisible.value = true;
  if (isCaptchaEnabled.value) {
    isCaptchaVisible.value = true;
  } else {
    showResponseForm();
  }
};

const showResponseForm = () => {
  createResponse(props.appealPublicReference, captchaValue.value)
      .then(function (response) {
        responsePublicReference.value = response.responsePublicReference;
        isResponseFormVisible.value = true;
      })
      .catch(function (error:ApplicationError) {
          return Promise.reject(error);
      });
};

function createResponse(appealPublicReference, captchaValue) {

  const parentDetails = {
    ParentPublicReference: appealPublicReference,
    ParentType: constants.responseParentType.appeal
  };

  return new Promise(function (resolve, reject) {
    responseService.createResponseWithCaptchaValidation(parentDetails, captchaValue)
        .then(function (response) {
          resolve(response);
        })
        .catch(function (error: ApplicationError) {

          logger.logAndThrowUntranslatedError(
              'An error occurred creating a response on appeal. Please try again later.',
              'Unable to get the response public identifier',
              error);

          reject(error);
        });
  });
}

onMounted(async () => {

  if (!props.appealPublicReference) {
    logger.logAndThrowUntranslatedError(
        'An error occurred loading the appeal details. Please try again later.',
        'No public reference found in URL'
    );
    return;
  }

  await retrieveAndPersistPortalSettings(contextDataStore);
  await retrieveAndPersistResourceData(contextDataStore);

  languageCode.value = contextDataStore.languageCode;
  isCaptchaEnabled.value = contextDataStore.portalSettings.appealResponseCaptchaEnabled;
  captchaPublicKey.value = contextDataStore.portalSettings.captchaPublicKey;
  isCaptchaVisible.value = isCaptchaEnabled.value;
  captchaValue.value = isCaptchaEnabled.value ? null : 'Captcha disabled';

  // Initialize each package which will result in backend calls being made for resource data and once
  // retrieved the resource helper being appended to with the package resource data.
  consoleLogInfo(
      contextDataStore.portalSettings,
      contextDataStore.languageCode
  );

  FlowWrapper.init();
});

function retrieveAndPersistPortalSettings(contextDataStore: ContextState) {
  return systemSettingsRepository
      .getPortalSettings()
      .then((portalSettings: PortalSettings) => {
        contextDataStore.inReleaseMode = portalSettings.inReleaseMode;
        contextDataStore.portalSettings = portalSettings;
      })
      .catch((error: ApplicationError) => {
        logger.logAndThrowUntranslatedError(
            'An error occurred loading the site. Please try again later.',
            'Unable to get the system portal settings',
            error
        );

        return Promise.reject(error);
      });
}

function retrieveAndPersistResourceData(contextDataStore: ContextState) {
  return resourceDataService
      .getResourceData(contextDataStore.languageCode)
      .then(resourceData => {
        contextDataStore.resourceData = resourceData;
      })
      .catch((error: ApplicationError) => {
      // As an error occurred retrieving the translated text, all we can do here is display error in English
        logger.logAndThrowUntranslatedError(
            'An error occurred loading the site. Please try again later.',
            'Unable to get the resource data / translated text',
            error
        );

        return Promise.reject(error);
      })
      .finally(() => {
        contextDataStore.registerPackageResourceData();
      });
}

function consoleLogInfo(portalSettings: PortalSettings, languageCode: string) {
  console.info('Thanks for visiting!');
  console.info('Version:', portalSettings.versionNumber);

  console.info('In release mode:', portalSettings.inReleaseMode);
  console.info('Language code:', languageCode);
}
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
